import { useEffect, useRef, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useHasUnsavedWork } from '../../hooks/useUnsavedWork';
import { hasUnsavedWork } from '../../utils/unsavedWork';

export default function PWAUpdatePrompt() {
  const registrationRef = useRef(null);
  const lastCheckRef = useRef(0);
  const [updating, setUpdating] = useState(false);
  // Applying an update reloads the page, which destroys anything held in
  // component state. Never offer that while work is in flight — a rep lost a
  // recorded meeting debrief to this banner on 2026-08-27 by accepting the
  // update mid-save (see src/utils/unsavedWork.js).
  const blockedByUnsavedWork = useHasUnsavedWork();
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, registration) {
      if (registration) registrationRef.current = registration;
    },
    onRegisterError(error) {
      console.error('SW registration error:', error);
    },
  });

  useEffect(() => {
    // Shared update check used by both the interval and the foreground
    // listeners. Debounced so rapid focus/visibility toggles don't hammer
    // the server (visibilitychange + focus often fire back-to-back).
    const checkForUpdate = async () => {
      const registration = registrationRef.current;
      if (!registration) return;
      // Skip when offline — the fetch is guaranteed to fail.
      if (navigator.onLine === false) return;
      const now = Date.now();
      if (now - lastCheckRef.current < 30 * 1000) return;
      lastCheckRef.current = now;
      try {
        await registration.update();
      } catch {
        // Transient failure (network blip, CDN 5xx, mid-deploy, or invalid
        // registration state); the next trigger retries.
      }
    };

    const intervalId = setInterval(checkForUpdate, 5 * 60 * 1000);

    // iOS Safari and installed PWAs freeze setInterval timers while the app
    // is backgrounded, and resume the same frozen page on reopen without
    // reloading — so the periodic check often never fires while the app is
    // foregrounded, and Safari users never see the update prompt. Re-check
    // whenever the app returns to the foreground to close that gap.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', checkForUpdate);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', checkForUpdate);
    };
  }, []);

  const handleUpdate = async () => {
    if (updating) return;
    // Re-check at click time, not just at render: work can start between the
    // render that showed this button and the click that lands on it.
    if (hasUnsavedWork()) return;
    setUpdating(true);
    try {
      await updateServiceWorker(true);
    } catch (error) {
      console.error('Service worker update failed:', error);
    }
    // Fallback reload in case the controllerchange-triggered reload doesn't fire
    // (e.g. when no waiting SW responds to SKIP_WAITING, or browser quirks).
    window.setTimeout(() => window.location.reload(), 2000);
  };

  // Suppressed, not dismissed: the banner reappears on its own as soon as the
  // pending work is saved or discarded, so the update is deferred rather than lost.
  if (!needRefresh || blockedByUnsavedWork) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm mb-[env(safe-area-inset-bottom)]">
      <p className="text-sm font-medium text-activ-navy mb-3">
        A new version of the app is available.
      </p>
      <button
        onClick={handleUpdate}
        disabled={updating}
        className="px-3 py-1.5 bg-activ-teal text-white text-sm font-medium rounded-lg hover:bg-activ-navy transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {updating ? 'Updating…' : 'Update now'}
      </button>
    </div>
  );
}
